import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Grid,
  Paper,
  TextField,
  Typography,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
} from "@mui/material";
import {
  isAuthenticated,
  addBookFee,
  getStudentInfo,
} from "../../../../ApiService/ApiService";
import { useSelector, useDispatch } from "react-redux";
import {
  fetchAcademicSessions,
  selectAllSessions,
  selectSessionsLoading,
  selectSessionsError,
} from "../../../../Slices/sessionsSlice";
import { ArrowBack, ArrowForward, Details } from "@mui/icons-material";
import CustomDialog from "../../../../Components/CustomDialog";

function AddBookFee({
  setShowAddBookFee,
  fetchBookFeeDetails,
  selectedStudentId,
  studentName,
  classes,
  selectedClassId,
}) {
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(fetchAcademicSessions());
  }, [dispatch]);
  const sessions = useSelector(selectAllSessions);
  const sessionsLoading = useSelector(selectSessionsLoading);
  const sessionsError = useSelector(selectSessionsError);

  const currentClass = classes?.data.find(
    (currClass) => currClass.id === selectedClassId
  );

  const currentClassName = `${currentClass?.grade} ${
    currentClass?.section ? currentClass?.section : ""
  }`;

  const currentDate = new Date().toISOString().slice(0, 10);

  const [formData, setFormData] = useState({
    studentId: selectedStudentId,
    classId: selectedClassId,
    academicSessionId: "",
    details: "",
    fromDate: "",
    toDate: "",
    amount: "",
    paidOn: currentDate,
    paymentMode: "Cash",
  });

  const [loading, setLoading] = useState(false);
  const [dialog, setDialog] = useState({
    visible: false,
    title: "",
    message: "",
    buttons: [],
  });

  const handleSessionChange = (event) => {
    const sessionId = event.target.value;
  };

  const handleDialogClose = () => {
    setDialog({ ...dialog, visible: false });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);

    try {
      const response = await addBookFee(formData);
      // console.log("RES", response?.data);
      if (response) {
        if (response?.data?.status === 200) {
          setDialog({
            visible: true,
            title: "Success",
            message: "Book Fee added",
            buttons: [{ name: "OK", onPress: handleDialogClose }],
          });
          fetchBookFeeDetails();
          setFormData({
            studentId: selectedStudentId,
            classId: selectedClassId,
            academicSessionId: "",
            details: "",
            fromDate: "",
            toDate: "",
            amount: "",
            paidOn: currentDate,
            paymentMode: "Cash",
          });
          setShowAddBookFee(false);
        } else {
          const errorMessage = response?.data?.errorMessage;
          setDialog({
            visible: true,
            title: "Error",
            message: errorMessage,
            buttons: [{ name: "Close", onPress: handleDialogClose }],
          });
        }
      }
    } catch (error) {
      setDialog({
        visible: true,
        title: "Error",
        message: "Something went wrong",
        buttons: [{ name: "Close", onPress: handleDialogClose }],
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <CustomDialog
        visible={dialog.visible}
        title={dialog.title}
        message={dialog.message}
        buttons={dialog.buttons}
        onClose={handleDialogClose}
      />
      <Box sx={{ maxWidth: "75%", margin: "auto", padding: 3 }}>
        <Paper elevation={3} sx={{ padding: 5 }}>
          <Typography
            variant="h6"
            gutterBottom
            sx={{ fontWeight: 600, fontSize: 22, color: "black" }}
          >
            Add Book Fee
          </Typography>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Student Name"
                  name="studentName"
                  value={studentName}
                  size="small"
                  required
                  disabled
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Class"
                  name="class"
                  value={currentClassName}
                  size="small"
                  required
                  disabled
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small" required>
                  <InputLabel id="session">Session</InputLabel>
                  <Select
                    fullWidth
                    name="sessionId"
                    label="Session"
                    labelId="session"
                    value={formData.academicSessionId}
                    onChange={(event) =>
                      setFormData({
                        ...formData,
                        academicSessionId: event.target.value,
                      })
                    }
                    size="small"
                    disabled={sessionsLoading || sessionsError}
                    required
                  >
                    {sessions?.data?.length > 0 ? (
                      sessions?.data?.map((currSession) => (
                        <MenuItem key={currSession?.id} value={currSession?.id}>
                          {currSession?.name}
                        </MenuItem>
                      ))
                    ) : (
                      <MenuItem disabled>Sessions unavailable</MenuItem>
                    )}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={8}>
                <TextField
                  fullWidth
                  label="Details"
                  name="details"
                  value={formData.details}
                  onChange={(event) =>
                    setFormData({ ...formData, details: event.target.value })
                  }
                  size="small"
                  required
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  name="amount"
                  type="number"
                  label="Amount"
                  value={formData.amount}
                  onChange={(event) =>
                    setFormData({
                      ...formData,
                      amount: parseFloat(event.target.value),
                    })
                  }
                  size="small"
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography sx={{ fontSize: 13, color: "gray" }}>
                  From Date *
                </Typography>
                <TextField
                  fullWidth
                  name="fromDate"
                  type="date"
                  labelid="From Date"
                  value={formData.fromDate}
                  onChange={(event) =>
                    setFormData({ ...formData, fromDate: event.target.value })
                  }
                  size="small"
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography sx={{ fontSize: 13, color: "gray" }}>
                  To Date *
                </Typography>
                <TextField
                  fullWidth
                  name="toDate"
                  type="date"
                  labelid="To Date"
                  value={formData.toDate}
                  onChange={(event) =>
                    setFormData({ ...formData, toDate: event.target.value })
                  }
                  size="small"
                  required
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  name="paidOn"
                  type="date"
                  labelid="paidOn"
                  label="Paid On"
                  value={formData?.paidOn}
                  onChange={(event) =>
                    setFormData({ ...formData, paidOn: event.target.value })
                  }
                  size="small"
                  sx={{ marginTop: 3 }}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl
                  fullWidth
                  required
                  size="small"
                  sx={{ marginTop: 3 }}
                >
                  <InputLabel id="paymode-label">Payment Mode</InputLabel>
                  <Select
                    labelId="paymode-label"
                    id="paymode"
                    value={formData?.paymentMode}
                    label="Payment Mode"
                    onChange={(event) =>
                      setFormData({
                        ...formData,
                        paymentMode: event.target.value,
                      })
                    }
                    required
                    size="small"
                  >
                    <MenuItem value={"Cash"}>Cash</MenuItem>
                    <MenuItem value={"Card"}>Card</MenuItem>
                    <MenuItem value={"UPI"}>UPI</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={12} md={6} lg={6}>
                <Button
                  fullWidth
                  variant="contained"
                  color="inherit"
                  onClick={() => setShowAddBookFee(false)}
                  startIcon={<ArrowBack />}
                  sx={{ textTransform: "unset" }}
                >
                  Back
                </Button>
              </Grid>
              <Grid item xs={12} sm={12} md={6} lg={6}>
                <Button
                  fullWidth
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={loading}
                  endIcon={<ArrowForward />}
                  sx={{ textTransform: "unset" }}
                >
                  {loading ? "Loading..." : "Submit"}
                </Button>
              </Grid>
            </Grid>
          </form>
        </Paper>
      </Box>
    </>
  );
}

export default AddBookFee;
